'use client';

import { useMemo } from 'react';

import { EBlockType } from '@/lib/enums';
import { IBlock, IDailyBonusItem } from '@/lib/types';

interface IUseAssembledBlocksProps {
  rawBlocks: IBlock[] | null;
  dailyBonusBlocks: IDailyBonusItem[];
  isLoggedIn: boolean;
}

interface IUseAssembledBlocksReturn {
  blocks: IBlock[] | null;
}

/**
 * Hook that merges daily bonus data into raw page blocks and filters blocks by login state.
 * Combines daily bonus items from popup offers with layout data from page blocks,
 * and removes blocks that should not be visible based on the user's authentication status.
 *
 * @example
 * const { blocks } = useAssembledBlocks({
 *   rawBlocks,
 *   dailyBonusBlocks,
 *   isLoggedIn: true,
 * });
 */
export const useAssembledBlocks = ({
  rawBlocks,
  dailyBonusBlocks,
  isLoggedIn,
}: IUseAssembledBlocksProps): IUseAssembledBlocksReturn => {
  const blocks = useMemo((): IBlock[] | null => {
    if (!rawBlocks || rawBlocks.length === 0) {
      return null;
    }

    const dailyBonusLayoutBlock = rawBlocks.find(
      (block) => block.type === EBlockType.DAILY_BONUS
    );

    const shouldMergeDailyBonus =
      isLoggedIn && dailyBonusBlocks.length > 0 && dailyBonusLayoutBlock;

    let result: IBlock[];

    if (shouldMergeDailyBonus) {
      const processedDailyBonusBlock: IBlock = {
        type: EBlockType.DAILY_BONUS,
        title: 'Daily Bonus',
        itemsPerRow: 1,
        items: dailyBonusBlocks.map((item) => ({
          ...item,
          backgroundImage: dailyBonusLayoutBlock.backgroundImage,
          backgroundImageMobile: dailyBonusLayoutBlock.backgroundImageMobile,
          redirectUrl: dailyBonusLayoutBlock.redirectUrl,
        })),
      };

      result = rawBlocks.map((block): IBlock =>
        block.type === EBlockType.DAILY_BONUS ? processedDailyBonusBlock : block
      );
    } else {
      result = rawBlocks.filter((block) => block.type !== EBlockType.DAILY_BONUS);
    }

    if (!isLoggedIn) {
      result = result.filter((block) => block.type !== EBlockType.OFFER);
    }

    return result;
  }, [rawBlocks, dailyBonusBlocks, isLoggedIn]);

  return { blocks };
};
